import React from 'react';
import { IconInstagram, IconLinkedIn, IconTwitter } from './icons/IconPack';

interface FooterProps {
  language: string;
}

const Footer: React.FC<FooterProps> = ({ language }) => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-blue-900 text-white py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <h3 className="text-heading-md mb-6 section-heading-underline">
              {language === 'zh' ? '迪衡數位科技' : 'DHT Solution'}
            </h3>
            <p className="text-body-md text-gray-300 mb-6">
              {language === 'zh' ? '提供變革性的人工智慧解決方案，推動創新和業務增長。' : 'Delivering transformative AI solutions that drive innovation and business growth.'}
            </p>
            <div className="flex space-x-4 mt-4">
              <a 
                href="https://www.instagram.com/DHT.Ai" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-800 rounded-lg flex items-center justify-center transition-standard hover:bg-blue-600"
                aria-label="Instagram"
              >
                <IconInstagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/company/dht-solution-limited-company" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-800 rounded-lg flex items-center justify-center transition-standard hover:bg-blue-600"
                aria-label="LinkedIn"
              >
                <IconLinkedIn className="w-5 h-5" />
              </a>
              <a 
                href="https://x.com/dhtsolution2018"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-blue-800 rounded-lg flex items-center justify-center transition-standard hover:bg-blue-600"
                aria-label="Twitter"
              >
                <IconTwitter className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          <div className="lg:col-span-1">
            <h3 className="text-heading-sm mb-6 section-heading-underline">
              {language === 'zh' ? '解決方案' : 'Solutions'}
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="#products" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? 'AI 開發' : 'AI Development'}
                </a>
              </li>
              <li>
                <a href="#products" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? '專業培訓' : 'Professional Training'}
                </a>
              </li>
              <li>
                <a href="#products" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? 'AI 基礎設施' : 'AI Infrastructure'}
                </a>
              </li>
              <li>
                <a href="#products" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? '戰略諮詢' : 'Strategic Consultation'}
                </a>
              </li>
            </ul>
          </div>
          
          <div className="lg:col-span-1">
            <h3 className="text-heading-sm mb-6 section-heading-underline">
              {language === 'zh' ? '公司' : 'Company'}
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="#company-profile" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? '公司簡介' : 'Company Profile'}
                </a>
              </li>
              <li>
                <a href="#careers" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? '工作機會' : 'Careers'}
                </a>
              </li>
              <li>
                <a href="#testimonials" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? '客戶評價' : 'Client Testimonials'}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-gray-300 hover:text-white transition-standard inline-block py-1">
                  {language === 'zh' ? '聯絡我們' : 'Contact Us'}
                </a>
              </li>
            </ul>
          </div>
          
          <div className="lg:col-span-1">
            <h3 className="text-heading-sm mb-6 section-heading-underline">
              {language === 'zh' ? '聯絡資訊' : 'Contact Information'}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-blue-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-gray-300">
                  {language === 'zh'
                    ? <>臺北市信義區<br/>忠孝東路5段<br/>1之1號9樓</>
                    : <>9F, No. 1, Sec. 5, Zhongxiao E. Rd.,<br />Xinyi Dist., Taipei City 110410,<br />Taiwan (R.O.C.)</>}
                </span>
              </li>
              <li className="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-blue-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-gray-300">administrator@dhtsolution.com</span>
              </li>
              <li className="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-blue-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-gray-300">+886 921-367-101</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-16 pt-8 border-t border-blue-800 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} DHT Solution. All rights reserved. Privacy Policy | Terms of Service
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;